import { useState, useEffect, useCallback, useRef } from 'react';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { app, db } from '../../firebase/firebaseConfig';

/* Ported verbatim from OLD Support/useSupportMessages.js.

   Live-subscribes to `supportConversations/{accountUUID}` and
   `supportConversations/{accountUUID}/messages` (ordered ascending by
   createdAt) so the caller sees new admin replies in real time.

   sendMessage(content, images, preGeneratedId?) writes:
     - supportConversations/{accountUUID}       (setDoc on first message,
                                                  updateDoc on subsequent)
     - supportConversations/{accountUUID}/messages/{id}
     Optimistically inserts a `pending:true` row into local state so the
     user sees their message immediately; the real doc replaces it when
     onSnapshot fires.

   markAsRead() updates `unreadByUser: 0` and sets `readAt` on every
   admin-authored message in the conversation that hasn't yet been read,
   so admin-side sees the double-tick.
*/
export function useSupportMessages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [conversationExists, setConversationExists] = useState(false);
  const [accountUUID, setAccountUUID] = useState(null);

  const auth = getAuth(app);
  const conversationUnsubRef = useRef(null);
  const messagesUnsubRef = useRef(null);

  useEffect(() => {
    const getUUID = async () => {
      const user = auth.currentUser;
      if (!user) { setLoading(false); return; }
      try {
        const tokenResult = await user.getIdTokenResult();
        setAccountUUID(tokenResult.claims.accountUUID || null);
      } catch (err) {
        console.error('useSupportMessages: error reading accountUUID', err);
        setError('Failed to authenticate');
        setLoading(false);
      }
    };
    getUUID();
  }, [auth]);

  useEffect(() => {
    if (!accountUUID) return undefined;

    setLoading(true);
    setError(null);

    const conversationRef = doc(db, 'supportConversations', accountUUID);
    conversationUnsubRef.current = onSnapshot(
      conversationRef,
      (snap) => {
        if (snap.exists()) {
          setConversationExists(true);
          setUnreadCount(snap.data().unreadByUser || 0);
        } else {
          setConversationExists(false);
          setUnreadCount(0);
        }
      },
      (err) => {
        if (err.code !== 'permission-denied') {
          console.error('useSupportMessages: conversation listener', err);
          setError('Failed to load conversation');
        }
      },
    );

    const messagesRef = collection(db, 'supportConversations', accountUUID, 'messages');
    const messagesQuery = query(messagesRef, orderBy('createdAt', 'asc'));
    messagesUnsubRef.current = onSnapshot(
      messagesQuery,
      (snapshot) => {
        const list = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          createdAt: d.data().createdAt?.toDate?.() || new Date(),
        }));
        setMessages(list);
        setLoading(false);
      },
      (err) => {
        if (err.code !== 'permission-denied') {
          console.error('useSupportMessages: messages listener', err);
          setError('Failed to load messages');
        }
        setLoading(false);
      },
    );

    return () => {
      if (conversationUnsubRef.current) conversationUnsubRef.current();
      if (messagesUnsubRef.current) messagesUnsubRef.current();
    };
  }, [accountUUID]);

  const sendMessage = useCallback(
    async (content, images = [], preGeneratedId = null) => {
      const trimmedContent = (content || '').trim();
      if ((!trimmedContent && images.length === 0) || !accountUUID) return;

      setSending(true);
      setError(null);

      // Preview — short text + optional [+N images] suffix.
      let preview;
      if (trimmedContent) {
        preview = trimmedContent.substring(0, 100);
        if (images.length > 0) preview += ` [+${images.length} image${images.length > 1 ? 's' : ''}]`;
      } else {
        preview = images.length === 1 ? '[Image]' : `[${images.length} Images]`;
      }

      const tempId = 'temp-' + Date.now();
      const optimisticMessage = {
        id: tempId,
        content: trimmedContent,
        senderType: 'user',
        senderEmail: auth.currentUser?.email,
        createdAt: new Date(),
        readAt: null,
        pending: true,
        ...(images.length > 0 && { images }),
      };
      setMessages((prev) => [...prev, optimisticMessage]);

      try {
        const user = auth.currentUser;
        if (!user) throw new Error('Not authenticated');

        const conversationRef = doc(db, 'supportConversations', accountUUID);
        const conversationSnap = await getDoc(conversationRef);

        if (!conversationSnap.exists()) {
          await setDoc(conversationRef, {
            createdAt: serverTimestamp(),
            lastMessageAt: serverTimestamp(),
            lastMessagePreview: preview,
            unreadByAdmin: 1,
            unreadByUser: 0,
            userEmail: user.email,
            status: 'open',
          });
        } else {
          const currentData = conversationSnap.data();
          await updateDoc(conversationRef, {
            lastMessageAt: serverTimestamp(),
            lastMessagePreview: preview,
            unreadByAdmin: (currentData.unreadByAdmin || 0) + 1,
            status: 'open',
          });
        }

        const messageData = {
          content: trimmedContent,
          senderType: 'user',
          senderEmail: user.email,
          createdAt: serverTimestamp(),
          readAt: null,
          ...(images.length > 0 && { images }),
        };

        const messagesRef = collection(db, 'supportConversations', accountUUID, 'messages');
        if (preGeneratedId) {
          await setDoc(doc(messagesRef, preGeneratedId), messageData);
        } else {
          await addDoc(messagesRef, messageData);
        }

        setMessages((prev) => prev.filter((m) => m.id !== tempId));
      } catch (err) {
        console.error('useSupportMessages: send error', err);
        setMessages((prev) => prev.filter((m) => m.id !== tempId));
        setError('Failed to send message. Please try again.');
      } finally {
        setSending(false);
      }
    },
    [auth, accountUUID],
  );

  const markAsRead = useCallback(async () => {
    if (!accountUUID) return;
    try {
      const conversationRef = doc(db, 'supportConversations', accountUUID);
      const conversationSnap = await getDoc(conversationRef);

      if (conversationSnap.exists() && conversationSnap.data().unreadByUser > 0) {
        await updateDoc(conversationRef, {
          unreadByUser: 0,
          lastReadByUserAt: serverTimestamp(),
        });

        const messagesRef = collection(db, 'supportConversations', accountUUID, 'messages');
        const messagesQuery = query(messagesRef, orderBy('createdAt', 'asc'));
        const messagesSnap = await getDocs(messagesQuery);

        const now = serverTimestamp();
        for (const messageDoc of messagesSnap.docs) {
          const data = messageDoc.data();
          if (data.senderType === 'admin' && !data.readAt) {
            await updateDoc(
              doc(db, 'supportConversations', accountUUID, 'messages', messageDoc.id),
              { readAt: now },
            );
          }
        }
      }
    } catch (err) {
      console.error('useSupportMessages: markAsRead error', err);
    }
  }, [accountUUID]);

  return {
    messages,
    loading,
    error,
    sending,
    unreadCount,
    conversationExists,
    accountUUID,
    sendMessage,
    markAsRead,
    clearError: () => setError(null),
  };
}
